import { TIMEZONE } from 'utils/formatTime';

// Enhanced logger with debug functionality
const isProduction = import.meta.env.NODE_ENV === 'production';

const MAX_LOG_HISTORY = 50;

const logHistory = [];

const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
};

// Enhanced error logging with stack traces
const addToLogHistory = (type, args) => {
  try {
    // Validate and sanitize args
    const safeArgs = Array.isArray(args) ? args : [args];

    const logEntry = {
      type,
      timestamp: new Date(),
      args: safeArgs,
      // Add stack trace for errors
      stack: type === 'error' ? new Error().stack : undefined,
      // Add URL context safely
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      // Add component context if available
      component: getCallerInfo(),
    };

    logHistory.push(logEntry);

    // Keep only the last MAX_LOG_HISTORY entries
    if (logHistory.length > MAX_LOG_HISTORY) {
      logHistory.shift(); // Remove the oldest entry
    }
  } catch (error) {
    // Fallback logging if addToLogHistory itself fails
    try {
      originalConsole.error('Logger failed:', error);
    } catch (fallbackError) {
      console.error('Logger failed:', fallbackError);
      // Last resort - silent fail to prevent infinite loops
    }
  }
};

// Intercept console calls with better error handling
console.error = (...args) => {
  try {
    addToLogHistory('error', args);
  } catch (error) {
    // If logging fails, still show the original error
    originalConsole.error('Logging failed:', error);
  }
  originalConsole.error(...args);
};

console.info = (...args) => {
  addToLogHistory('info', args);
  originalConsole.info(...args);
};

// Add method to retrieve logs
export const getLogHistory = () => logHistory;
export const getErrorLogHistory = () => {
  return logHistory.filter((log) => log.type === 'error');
};

// Browser-specific console colors using CSS
const browserColors = {
  error: 'color: #FF0000',
  warn: 'color: #FFA500',
  info: 'color: #0000FF',
  debug: 'color: #808080',
};

// Get current timestamp
const getTimestamp = () => {
  return new Date().toLocaleString('en-US', {
    timeZone: TIMEZONE.JAPAN,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const getCallerInfo = () => {
  const stackObj = {};
  Error.captureStackTrace(stackObj, getCallerInfo);

  // Get the stack trace lines
  const stackLines = stackObj.stack.split('\n');

  // Find the first non-logger call
  for (const line of stackLines) {
    // Skip logger.js and internal calls
    if (
      line.includes('logger.js') ||
      line.includes('at Object.') ||
      line.includes('at Function.')
    ) {
      continue;
    }

    // Try to extract the component name or file name
    const matches = line.trim().match(/at\s+(\w+[\w]*)\s+/);
    if (matches && matches[1]) {
      return matches[1]; // Return just the function/component name
    }
  }

  return 'unknown';
};

// Create a logger factory
const logger = {
  createLogger: (component) => {
    const logInstance = {
      debug: (...args) => {
        if (!isProduction) {
          const [message, ...rest] = args;
          const caller = getCallerInfo();

          // Use console.log with multiple arguments for better object inspection
          console.log(
            `[${getTimestamp()}] %c[DEBUG]%c [${component}] <${caller}> ${message}`,
            browserColors.debug,
            'color: inherit',
            ...rest
          );
        }
      },
      log: (...args) => {
        const [message, ...rest] = args;

        // Use console.log with multiple arguments for better object inspection
        console.log(
          `[${getTimestamp()}] %c[INFO]%c [${component}] ${message}`,
          browserColors.info,
          'color: inherit',
          ...rest
        );
      },
      info: (...args) => {
        const [message, ...rest] = args;

        // Use console.info with multiple arguments for better object inspection
        console.info(
          `[${getTimestamp()}] %c[INFO]%c [${component}] ${message}`,
          browserColors.info,
          'color: inherit',
          ...rest
        );
      },
      warn: (...args) => {
        const [message, ...rest] = args;

        // Use console.warn with multiple arguments for better object inspection
        console.warn(
          `[${getTimestamp()}] %c[WARN]%c [${component}] ${message}`,
          browserColors.warn,
          'color: inherit',
          ...rest
        );
      },
      error: (...args) => {
        const [message, ...rest] = args;
        const caller = getCallerInfo();

        // Create enhanced error object
        const errorInfo = {
          message,
          caller,
          timestamp: getTimestamp(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          // Add any additional context
          ...(rest.length > 0 && { additionalData: rest }),
        };

        // Use console.error with multiple arguments for better object inspection
        console.error(
          `[${getTimestamp()}] %c[ERROR]%c [${component}] <${caller}> ${message}`,
          browserColors.error,
          'color: inherit',
          ...rest
        );

        // Log to history with enhanced context
        addToLogHistory('error', [errorInfo]);
      },
    };

    return logInstance;
  },
};

export default logger;
